import React, { useState } from "react";
import { CsI18n } from "../shared/csI18n/csI18n";
import {
  Banner,
  Card,
  Collapsible,
  Heading,
  Icon,
  Layout,
  Loading,
  Page,
  PageActions,
  Spinner,
  Stack
} from "@shopify/polaris";
import UserGuideResources from "./resources/UserGuideResources";
import ReactMarkdown from "react-markdown";
import LegalAppBridge from "../LegalAppBridge";
import "./../styles/documentation.scss";
import { ChevronDownMinor, ChevronUpMinor } from "@shopify/polaris-icons";
import Env from "../Env";
import CsLocalStorage from "../CsLocalStorage";
import CsConstant from "../CsConstant";

function UserGuideLoading() {
  return (
    <Stack distribution="center">
      <Spinner size="large" color="teal" accessibilityLabel={CsI18n.t("Loading")} />
    </Stack>
  );
}

function UserGuideError(props) {
  return (
    <Banner status='critical' title={CsI18n.t('Failed to fetch user guide')}>
      <p>{props.error}</p>
    </Banner>
  );
}

function UserGuideItem(props) {
  const [open, setOpen] = useState(false);
  const handleToggleCard = () => setOpen(!open);

  return (
    <Card sectioned>
      <div className="clickable-documentation-card" onClick={handleToggleCard}>
        <Stack>
          <Stack.Item fill><Heading element='h3'>{props.title}</Heading></Stack.Item>
          <Stack.Item>
            <Icon source={open ? ChevronUpMinor : ChevronDownMinor} />
          </Stack.Item>
        </Stack>
      </div>

      <Collapsible id="some_id" open={open}>
        <ReactMarkdown source={props.content} />
      </Collapsible>
    </Card>
  );
}

function UserGuideContent(props) {
  return props.contents.map((item, index) => {
    return <UserGuideItem title={item.title} content={item.content} key={index} />
  });
}

class UserGuide extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      needToFetch: this.props.getUserGuide().length < 1,
      fetchError: false,
      error: ''
    }

    this.resources = new UserGuideResources(
      this.props.lang,
      Env.log,
      (cacheKey) => {
        Env.log('---Documentation: Get cache', cacheKey);
        return CsLocalStorage.loadStorageByExpiredTime(cacheKey, CsConstant.DOCS_EXPIRE_TIME);
      },
      (cacheKey, data) => {
        Env.log('---Documentation: Set cache', cacheKey, data.length);
        CsLocalStorage.saveStorageWithTimestamp(cacheKey, data);
      },
      (cacheKey) => {
        Env.log('---Documentation: Remove cache', cacheKey);
        CsLocalStorage.removeStorage(cacheKey);
      },
      Env.getRefreshDocumentation()
    )
  }

  loadDoc = () => {
    this.resources.loadResources()
      .then(result => {
        if (Array.isArray(result) && result.length) {
          this.props.setUserGuide(result);
        }
      })
      .catch((error) => {
        LegalAppBridge.toastError('Failed to fetch user guide!');
        this.setState({ fetchError: true, error: error.toString() });
      })
      .finally(() => this.setState({ needToFetch: false }));
  }

  componentDidMount() {
    if (this.state.needToFetch) {
      this.loadDoc();
    }
  }

  render() {
    let mainContent;
    const markdowns = this.props.getUserGuide();

    if (this.state.needToFetch) {
      mainContent = <><Loading /><UserGuideLoading /></>;
    } else {
      if (this.state.fetchError) {
        mainContent = <UserGuideError error={this.state.error} />
      } else {
        mainContent = <UserGuideContent contents={markdowns} />;
      }
    }

    return (
      <Page title={CsI18n.t('title.user_guide')} fullwidth>
        <Layout>
          <Layout.Section>
            <div className="cs-documentation">
              {mainContent}
            </div>
          </Layout.Section>
        </Layout>

        {Env.isDevMode() &&
        <Layout>
          <Layout.Section>
            <PageActions secondaryActions={[{ content: 'Force reload doc', onAction: this.loadDoc }]} />
          </Layout.Section>
        </Layout>}
      </Page>
    );
  }
}

export default UserGuide;
